import { nsContract, nsTerminal } from '@/app/i18n/namespaces';
import { FILTER_TYPES } from '@/common-utils/components/renders/dataTable/filter/FilterTypes';
import { FIELD_TYPES } from '@/common-utils/components/renders/detailsGrid/FieldTypes';
import OPERATION_TYPE from '../enums/OperationType';
import APPLICATIONS from '../../contracts/enums/ItpRegex';
import CB_VERSION_TYPE from '../../contracts/enums/CbVersionType';
import NETWORK_INTERFACE_TYPE from '../enums/NetworkInterfaceType';

export default {
    UUID: {
        key: 'uuid'
    },
    NETWORK_INFORMATION: {
        key: 'networkInformation',
        SOURCE_ADDRESS: {
            parentKeys: ['networkInformation'],
            key: 'sourceAddress',
            type: FIELD_TYPES.TEXT,
            namespace: nsTerminal,
            entity: 'keys.event_logs.network_information.source_address',
            isNested: true
        },
        SOURCE_PORT: {
            parentKeys: ['networkInformation'],
            key: 'sourcePort',
            type: FIELD_TYPES.TEXT,
            namespace: nsTerminal,
            entity: 'keys.event_logs.network_information.source_port',
            isNested: true
        },
        ENTRY_ADDRESS: {
            parentKeys: ['networkInformation'],
            key: 'entryAddress',
            type: FIELD_TYPES.TEXT,
            namespace: nsTerminal,
            entity: 'keys.event_logs.network_information.entry_address',
            isNested: true
        },
        ENTRY_PORT: {
            parentKeys: ['networkInformation'],
            key: 'entryPort',
            type: FIELD_TYPES.TEXT,
            namespace: nsTerminal,
            entity: 'keys.event_logs.network_information.entry_port',
            isNested: true,
            filter: FILTER_TYPES.INPUT,
            enableSorting: true,
            placeholder: 'filter.placeholder.event_logs.entry_port'
        },
        SSL_VERSION: {
            parentKeys: ['networkInformation'],
            key: 'sslVersion',
            type: FIELD_TYPES.TEXT,
            namespace: nsTerminal,
            entity: 'keys.event_logs.network_information.ssl_version',
            isNested: true
        },
        ORIGIN_PORT: {
            parentKeys: ['networkInformation'],
            key: 'originPort',
            type: FIELD_TYPES.TEXT,
            namespace: nsTerminal,
            entity: 'keys.event_logs.network_information.origin_port',
            isNested: true
        }
    },
    DEVICE_INFORMATION: {
        key: 'deviceInformation',
        MANUFACTURER_CODE: {
            parentKeys: ['deviceInformation'],
            key: 'manufacturerCode',
            type: FIELD_TYPES.TEXT,
            namespace: nsTerminal,
            entity: 'keys.event_logs.device_information.manufacturer_code',
            isNested: true
        },
        PRODUCT_CODE: {
            parentKeys: ['deviceInformation'],
            key: 'productCode',
            type: FIELD_TYPES.TEXT,
            namespace: nsTerminal,
            entity: 'keys.event_logs.device_information.product_code',
            isNested: true
        },
        SERIAL_NUMBER: {
            parentKeys: ['deviceInformation'],
            key: 'serialNumber',
            type: FIELD_TYPES.TEXT,
            namespace: nsTerminal,
            entity: 'keys.event_logs.device_information.serial_number',
            isNested: true
        },
        NETWORK_INTERFACE: {
            parentKeys: ['deviceInformation'],
            key: 'networkInterface',
            type: FIELD_TYPES.TEXT,
            namespace: nsTerminal,
            entity: 'keys.event_logs.device_information.network_interface',
            isNested: true
        },
        NETWORK_INTERFACE_TYPE: {
            parentKeys: ['deviceInformation'],
            key: 'networkInterfaceType',
            type: FIELD_TYPES.TEXT,
            namespace: nsTerminal,
            entity: 'keys.event_logs.device_information.network_interface_type',
            isNested: true,
            filter: FILTER_TYPES.SELECT,
            enableSorting: true,
            enum: NETWORK_INTERFACE_TYPE
        },
        ICCID: {
            parentKeys: ['deviceInformation'],
            key: 'iccid',
            type: FIELD_TYPES.TEXT,
            namespace: nsTerminal,
            entity: 'keys.event_logs.device_information.iccid',
            isNested: true
        },
        ICCID_OPERATOR: {
            parentKeys: ['deviceInformation'],
            key: 'iccidOperator',
            type: FIELD_TYPES.TEXT,
            namespace: nsTerminal,
            entity: 'keys.event_logs.device_information.iccid_operator',
            isNested: true
        },
        POS_TERMINAL_UUID: {
            parentKeys: ['deviceInformation'],
            key: 'posTerminalUuid',
            type: FIELD_TYPES.TEXT,
            namespace: nsTerminal,
            entity: 'keys.event_logs.device_information.pos_terminal_uuid',
            isNested: true
        }
    },
    DESTINATION_INFORMATION: {
        key: 'destinationInformation',
        ROUTE: {
            parentKeys: ['destinationInformation'],
            key: 'route',
            type: FIELD_TYPES.TEXT,
            namespace: nsTerminal,
            entity: 'keys.event_logs.destination_information.route',
            isNested: true
        },
        ADDRESS: {
            key: 'address',
            X121_ADDRESS: {
                parentKeys: ['destinationInformation', 'address'],
                key: 'x121Address',
                type: FIELD_TYPES.TEXT,
                namespace: nsTerminal,
                entity: 'keys.event_logs.destination_information.address.x121_address',
                isNested: true,
                filter: FILTER_TYPES.INPUT,
                enableSorting: true,
                placeholder: 'filter.placeholder.event_logs.x121_address'
            },
            ADDRESS_TYPE: {
                parentKeys: ['destinationInformation', 'address'],
                key: 'addressType',
                type: FIELD_TYPES.TEXT,
                namespace: nsTerminal,
                entity: 'keys.event_logs.destination_information.address.address_type',
                isNested: true
            }
        }
    },
    FINANCIAL_INFORMATION: {
        key: 'financialInformation',
        OPERATIONS: {
            parentKeys: ['financialInformation'],
            key: 'operations',
            type: FIELD_TYPES.TEXT,
            prefix: 'operationType_',
            namespace: nsTerminal,
            entity: 'keys.event_logs.financial_information.operations',
            isNested: true,
            filter: FILTER_TYPES.SELECT,
            enableSorting: true,
            enum: OPERATION_TYPE
        },
        ACCEPTANCE_SYSTEM_INFORMATION: {
            key: 'acceptanceSystemInformation',
            APPLICATION_ID: {
                parentKeys: ['financialInformation', 'acceptanceSystemInformation'],
                key: 'applicationId',
                type: FIELD_TYPES.TEXT,
                namespace: nsTerminal,
                entity: 'keys.event_logs.financial_information.acceptance_system_information.application_id',
                isNested: true,
                filter: FILTER_TYPES.INPUT,
                enableSorting: true,
                placeholder: 'filter.placeholder.event_logs.application_id'
            },
            ACCEPTANCE_SYSTEM_ID: {
                parentKeys: ['financialInformation', 'acceptanceSystemInformation'],
                key: 'acceptanceSystemId',
                type: FIELD_TYPES.TEXT,
                namespace: nsTerminal,
                entity: 'keys.event_logs.financial_information.acceptance_system_information.acceptance_system_id',
                isNested: true
            },
            ACCEPTANCE_SYSTEM_NUMBER: {
                parentKeys: ['financialInformation', 'acceptanceSystemInformation'],
                key: 'acceptanceSystemNumber',
                type: FIELD_TYPES.TEXT,
                namespace: nsTerminal,
                entity: 'keys.event_logs.financial_information.acceptance_system_information.acceptance_system_number',
                isNested: true,
                filter: FILTER_TYPES.INPUT,
                enableSorting: true,
                placeholder: 'filter.placeholder.event_logs.acceptance_system_number'
            },
            ACQUIRER_IDENTIFIER: {
                parentKeys: ['financialInformation', 'acceptanceSystemInformation'],
                key: 'acquirerIdentifier',
                type: FIELD_TYPES.TEXT,
                namespace: nsTerminal,
                entity: 'keys.event_logs.financial_information.acceptance_system_information.acquirer_identifier',
                isNested: true
            },
            BANK_CODE: {
                parentKeys: ['financialInformation', 'acceptanceSystemInformation'],
                key: 'bankCode',
                type: FIELD_TYPES.TEXT,
                namespace: nsTerminal,
                entity: 'keys.event_logs.financial_information.acceptance_system_information.bank_code',
                isNested: true,
                filter: FILTER_TYPES.INPUT,
                enableSorting: true,
                placeholder: 'filter.placeholder.event_logs.bank_code'
            },
            ACCEPTANCE_AGREEMENT_NUMBER: {
                parentKeys: ['financialInformation', 'acceptanceSystemInformation'],
                key: 'acceptanceAgreementNumber',
                type: FIELD_TYPES.TEXT,
                namespace: nsTerminal,
                entity: 'keys.event_logs.financial_information.acceptance_system_information.acceptance_agreement_number',
                isNested: true,
                filter: FILTER_TYPES.INPUT,
                enableSorting: true,
                placeholder: 'filter.placeholder.event_logs.acceptance_agreement_number'
            },
            COMPANY_REGISTRATION_NUMBER: {
                parentKeys: ['financialInformation', 'acceptanceSystemInformation'],
                key: 'companyRegistrationNumber',
                type: FIELD_TYPES.TEXT,
                namespace: nsTerminal,
                entity: 'keys.event_logs.financial_information.acceptance_system_information.company_registration_number',
                isNested: true,
                filter: FILTER_TYPES.INPUT,
                enableSorting: true,
                placeholder: 'filter.placeholder.event_logs.company_registration_number'
            }
        },
        SESSION_PROTOCOL_INFORMATION: {
            key: 'sessionProtocolInformation',
            NAME: {
                parentKeys: ['financialInformation', 'sessionProtocolInformation'],
                key: 'name',
                type: FIELD_TYPES.TEXT,
                namespace: nsTerminal,
                entity: 'keys.event_logs.financial_information.session_protocol_information.name',
                isNested: true
            },
            VERSION: {
                parentKeys: ['financialInformation', 'sessionProtocolInformation'],
                key: 'version',
                type: FIELD_TYPES.TEXT,
                namespace: nsTerminal,
                entity: 'keys.event_logs.financial_information.session_protocol_information.version',
                isNested: true
            }
        }
    },
    ERROR_INFORMATION: {
        key: 'errorInformation',
        type: FIELD_TYPES.TEXT,
        namespace: nsTerminal,
        entity: 'keys.event_logs.error_information'
    },
    METRICS_INFORMATION: {
        key: 'metricsInformation',
        STARTED_AT: {
            parentKeys: ['metricsInformation'],
            key: 'startedAt',
            type: FIELD_TYPES.DATE,
            namespace: nsTerminal,
            entity: 'keys.event_logs.metrics_information.started_at',
            isNested: true,
            filter: FILTER_TYPES.DATE,
            enableSorting: true
        },
        DURATION: {
            parentKeys: ['metricsInformation'],
            key: 'duration',
            type: FIELD_TYPES.TEXT,
            namespace: nsTerminal,
            entity: 'keys.event_logs.metrics_information.duration',
            isNested: true
        }
    },
    CREATED_AT: {
        key: 'createdAt',
        type: FIELD_TYPES.DATE,
        namespace: nsTerminal,
        entity: 'keys.event_logs.created_at'
    },
    UPDATED_AT: {
        key: 'updatedAt',
        type: FIELD_TYPES.DATE,
        namespace: nsTerminal,
        entity: 'keys.event_logs.updated_at'
    },
    VERSION_CB: {
        key: 'cbVersion',
        type: FIELD_TYPES.TEXT,
        namespace: nsTerminal,
        entity: 'keys.event_logs.version_cb',
        prefix: 'cbVersionType_',
        filter: FILTER_TYPES.SELECT,
        enableSorting: true,
        enum: CB_VERSION_TYPE
    },
    APPLICATION: {
        key: 'application',
        type: FIELD_TYPES.TEXT,
        prefix: 'posTerminalApplication_',
        namespace: nsContract,
        entity: 'keys.application',
        filter: FILTER_TYPES.SELECT,
        enableSorting: true,
        enum: APPLICATIONS,
        sortOptions: true
    }
};